import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import type { jsPDF } from 'jspdf';
import {
  TiptapJSON,
  SignatureData,
  SignerEntry,
  ReportTemplate,
  ReportBindingData,
} from '../types/template.types';
import { PDF_PRINT_STYLES } from './pdf-print.styles';
import { extractCurrentPageStyles } from '@utils/pdf-utils';

/**
 * PDF Export configuration options for html2pdf
 */
export interface Html2PdfExportOptions {
  /** Tiptap JSON content of the editor */
  content: TiptapJSON;
  /** Report template for header information */
  template: ReportTemplate;
  /** Signature data (legacy) */
  signatures?: Record<string, SignatureData>;
  /** Dynamic signer list - rendered into the signatures section of the PDF */
  signers?: SignerEntry[];
  /** Filename for the exported PDF (without extension) */
  filename?: string;
  /** Whether to include page numbers */
  includePageNumbers?: boolean;
  /**
   * Controls header placement across pages.
   *
   * - `true`  – The header is captured as an image and stamped on **every** page
   *             via jsPDF.  The header is removed from the rendered HTML content
   *             so it does not appear twice on page 1.  Top margin is increased
   *             automatically to make room for the header image.
   * - `false` (default) – The header remains in the HTML content and appears
   *             naturally on **page 1 only**.  Pages 2+ show the compact patient
   *             info box instead (when `bindingData` is provided).
   */
  repeatHeader?: boolean;
  /** Image quality (0-1) */
  imageQuality?: number;
  /** Binding data used to populate the patient info box on pages 2+ */
  bindingData?: ReportBindingData;
}

// =============================================================================
// PDF Export Constants
// =============================================================================

/** A4 page dimensions and margins in millimeters */
const PDF_DIMENSIONS = {
  pageWidth: 210,
  pageHeight: 297,
  marginTop: 10,
  marginBottom: 20,
  marginLeft: 15,
  marginRight: 15,
  get contentWidth(): number {
    return this.pageWidth - this.marginLeft - this.marginRight;
  },
} as const;

/** Gap between repeated header image and page content (mm) */
const HEADER_CONTENT_GAP_MM = 5;

/** html2canvas configuration for PDF rendering */
const HTML2CANVAS_CONFIG = {
  scale: 3,
  useCORS: true,
  backgroundColor: '#ffffff',
  logging: false,
} as const;

const pxToMm = 25.4 / 96 / 3;

// =============================================================================
// Capture interfaces
// =============================================================================

/**
 * Captured header image data for repetition on all pages
 */
interface HeaderCapture {
  /** Base64 image data URL */
  imageData: string;
  /** Original canvas width in pixels */
  width: number;
  /** Original canvas height in pixels */
  height: number;
  /** Height in mm when scaled to content width */
  scaledHeightMm: number;
}

/**
 * Captured footer image data for repetition on all pages
 */
interface FooterCapture {
  /** Base64 image data URL */
  imageData: string;
  /** Original canvas width in pixels */
  width: number;
  /** Original canvas height in pixels */
  height: number;
  /** Height in mm when scaled to content width */
  scaledHeightMm: number;
}

/**
 * Captured patient info box image data for pages 2+
 */
interface PatientInfoCapture {
  /** Base64 image data URL */
  imageData: string;
  /** Width in mm */
  widthMm: number;
  /** Height in mm */
  heightMm: number;
}

/**
 * Captured page number image data
 */
interface PageNumberCapture {
  /** Base64 image data URL */
  imageData: string;
  /** Width in mm when rendered */
  widthMm: number;
  /** Height in mm when rendered */
  heightMm: number;
}

// =============================================================================
// Capture functions
// =============================================================================

/**
 * Capture the header element as an image for repetition on subsequent pages.
 * Uses the print-styled container to ensure consistent appearance.
 * @param printContainer - The print container element with styled content
 * @returns HeaderCapture object or null if no header found
 */
async function captureHeaderAsImage(
  printContainer: HTMLElement
): Promise<HeaderCapture | null> {
  const headerElement = printContainer.querySelector(
    '[data-section-type="header"]'
  );
  if (!headerElement) {
    return null;
  }

  try {
    const canvas = await html2canvas(
      headerElement as HTMLElement,
      HTML2CANVAS_CONFIG
    );
    const scaledHeightMm =
      (canvas.height * PDF_DIMENSIONS.contentWidth) / canvas.width;

    return {
      imageData: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height,
      scaledHeightMm,
    };
  } catch (error) {
    console.warn('Failed to capture header as image:', error);
    return null;
  }
}

/**
 * Capture the footer element as an image for repetition on all pages.
 * Uses the print-styled container to ensure consistent appearance.
 * @param printContainer - The print container element with styled content
 * @returns FooterCapture object or null if no footer found
 */
async function captureFooterAsImage(
  printContainer: HTMLElement
): Promise<FooterCapture | null> {
  const footerElement = printContainer.querySelector(
    '[data-section-type="footer"]'
  );
  if (!footerElement) {
    return null;
  }

  try {
    const canvas = await html2canvas(
      footerElement as HTMLElement,
      HTML2CANVAS_CONFIG
    );
    const scaledHeightMm =
      (canvas.height * PDF_DIMENSIONS.contentWidth) / canvas.width;

    return {
      imageData: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height,
      scaledHeightMm,
    };
  } catch (error) {
    console.warn('Failed to capture footer as image:', error);
    return null;
  }
}

/**
 * Format ISO date string to Greek locale format for PDF display
 * @param dateString - ISO date string to format
 * @returns Formatted date string in Greek locale (dd/mm/yyyy)
 */
function formatDateForPdf(dateString?: string): string {
  if (!dateString) {
    return '-';
  }
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Create a compact horizontal patient info box image for placement on pages 2+.
 * Displays patient name, date of birth, and order ID side by side in a bordered box.
 * @param bindingData - Report binding data containing patient and order info
 * @returns PatientInfoCapture object or null on failure
 */
async function createPatientInfoImage(
  bindingData: ReportBindingData
): Promise<PatientInfoCapture | null> {
  const { patient, order } = bindingData;
  const name = patient.name || '-';
  const birthDate = formatDateForPdf(patient.birthDate);
  const orderId = order.identifier || order.id || '-';

  // Convert content width from mm to px (at 96dpi, scale:1)
  const contentWidthPx = Math.round(PDF_DIMENSIONS.contentWidth / pxToMm);

  const tempDiv = document.createElement('div');
  tempDiv.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    background: white;
    width: ${contentWidthPx}px;
    border: 1px solid #333;
    padding: 4px 10px;
    font-family: Cambria, 'Times New Roman', Times, serif;
    font-size: 24pt;
    display: flex;
    flex-direction: row;
    align-items: center;
    box-sizing: border-box;
  `;
  tempDiv.innerHTML = `
    <span style="flex: 1; padding-left: 20px; padding-bottom: 40px;"><strong>Ασθενής:</strong> ${name}</span>
    <span style="flex: 1; border-left: 1px solid #000; padding-left: 20px; padding-bottom: 34px;"><strong>Ημ. Γέννησης:</strong> ${birthDate}</span>
    <span style="flex: 1; border-left: 1px solid #000; padding-left: 20px; padding-bottom: 34px;"><strong>Αρ. Παραγγελίας:</strong> ${orderId}</span>
  `;
  document.body.appendChild(tempDiv);

  try {
    const canvas = await html2canvas(tempDiv, HTML2CANVAS_CONFIG);
    const widthMm = PDF_DIMENSIONS.contentWidth;
    const heightMm = (canvas.height * widthMm) / canvas.width;
    return {
      imageData: canvas.toDataURL('image/png'),
      widthMm,
      heightMm,
    };
  } catch (error) {
    console.warn('Failed to create patient info image:', error);
    return null;
  } finally {
    document.body.removeChild(tempDiv);
  }
}

/**
 * Create a page number image using html2canvas
 * @param pageNum - Current page number
 * @param totalPages - Total number of pages
 * @returns PageNumberCapture object
 */
async function createPageNumberImage(
  pageNum: number,
  totalPages: number
): Promise<PageNumberCapture> {
  const tempDiv = document.createElement('div');
  tempDiv.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    background: white;
    padding: 4px 12px;
    font-family: Cambria, 'Times New Roman', Times, serif;
    font-size: 9pt;
    color: #666666;
    white-space: nowrap;
  `;
  tempDiv.textContent = `Σελίδα ${pageNum} από ${totalPages}`;
  document.body.appendChild(tempDiv);

  try {
    const canvas = await html2canvas(tempDiv, HTML2CANVAS_CONFIG);
    const widthMm = canvas.width * pxToMm;
    const heightMm = canvas.height * pxToMm;

    return {
      imageData: canvas.toDataURL('image/png'),
      widthMm,
      heightMm,
    };
  } finally {
    document.body.removeChild(tempDiv);
  }
}

// =============================================================================
// PDF overlay placement
// =============================================================================

/**
 * Add per-page overlays to the PDF:
 * - Header image on ALL pages when `repeatHeader` is true
 * - Patient info box on pages 2+ when `repeatHeader` is false and info is available
 * - Page number above the footer on every page
 * - Footer on every page (bottom margin area)
 *
 * @param pdf - The jsPDF instance
 * @param header - Captured header image data (null if not repeating header)
 * @param footer - Captured footer image data (null if no footer)
 * @param patientInfo - Captured patient info image (null if not provided)
 * @param options - Options controlling which overlays to add
 * @param options.includePageNumbers - Whether to add page numbers to each page
 * @param options.repeatHeader - Whether the header is being repeated on all pages
 * @param options.headerOnPage1Only - Whether to stamp the header on page 1 only (patient-info mode)
 */
async function addPdfOverlays(
  pdf: jsPDF,
  header: HeaderCapture | null,
  footer: FooterCapture | null,
  patientInfo: PatientInfoCapture | null,
  options: {
    includePageNumbers?: boolean;
    repeatHeader?: boolean;
    /**
     * When true the header image is stamped on page 1 only (header was
     * extracted from content so that page 1 and pages 2+ share the same
     * top margin and the header starts at the same Y as the patient info box).
     */
    headerOnPage1Only?: boolean;
  }
): Promise<void> {
  const totalPages = pdf.getNumberOfPages();

  // Pre-calculate footer position (same for all pages)
  const footerBottomPadding = 5; // mm from the bottom edge of the page
  const footerHeightMm = footer ? footer.scaledHeightMm : 0;
  const footerY = footer
    ? PDF_DIMENSIONS.pageHeight - footerHeightMm - footerBottomPadding
    : null;

  for (let page = 1; page <= totalPages; page++) {
    pdf.setPage(page);

    // ── Header stamp ─────────────────────────────────────────────────────────
    // repeatHeader=true  → header on every page
    // headerOnPage1Only  → header on page 1 only (patient-info mode, header
    //                       extracted from content so both overlays share the
    //                       same marginTop Y position)
    const stampHeader =
      (options.repeatHeader && header) ||
      (options.headerOnPage1Only && header && page === 1);

    if (stampHeader && header) {
      pdf.addImage(
        header.imageData,
        'PNG',
        PDF_DIMENSIONS.marginLeft,
        PDF_DIMENSIONS.marginTop,
        PDF_DIMENSIONS.contentWidth,
        header.scaledHeightMm
      );
    }

    // ── Patient info box on pages 2+ (when repeatHeader: false) ──────────────
    if (!options.repeatHeader && page > 1 && patientInfo) {
      pdf.addImage(
        patientInfo.imageData,
        'PNG',
        PDF_DIMENSIONS.marginLeft,
        PDF_DIMENSIONS.marginTop,
        patientInfo.widthMm,
        patientInfo.heightMm
      );
    }

    // ── Page number (above the footer) ───────────────────────────────────────
    if (options.includePageNumbers && totalPages > 1) {
      const pageNumberImg = await createPageNumberImage(page, totalPages);
      const xPosition = (PDF_DIMENSIONS.pageWidth - pageNumberImg.widthMm) / 2;

      // Position page number just above the footer (or centered in bottom margin
      // when there is no footer)
      const pageNumGapAboveFooter = 2; // mm between page number and footer
      const pageNumY =
        footerY !== null
          ? footerY - pageNumberImg.heightMm - pageNumGapAboveFooter
          : PDF_DIMENSIONS.pageHeight -
            PDF_DIMENSIONS.marginBottom / 2 -
            pageNumberImg.heightMm / 2;

      pdf.addImage(
        pageNumberImg.imageData,
        'PNG',
        xPosition,
        pageNumY,
        pageNumberImg.widthMm,
        pageNumberImg.heightMm
      );
    }

    // ── Footer on every page (below the page number) ─────────────────────────
    if (footer && footerY !== null) {
      pdf.addImage(
        footer.imageData,
        'PNG',
        PDF_DIMENSIONS.marginLeft,
        footerY,
        PDF_DIMENSIONS.contentWidth,
        footer.scaledHeightMm
      );
    }
  }
}

// =============================================================================
// Print container helpers
// =============================================================================

/**
 * Build the HTML for the signatures section from a list of signers.
 * Signers are shown side-by-side (flex row): name on the first line, title below.
 * @param signers - Array of signer entries to render
 * @returns HTML string for the signatures block, or empty string if no signers have data
 */
function buildSignersHtml(signers: SignerEntry[]): string {
  const populated = signers.filter((s) => s.name || s.title);
  if (populated.length === 0) {
    return '';
  }

  const blockStyle = 'flex:1;text-align:center;padding:0 8px;';
  const nameStyle =
    "font-family:Cambria,'Times New Roman',Times,serif;font-size:10pt;";
  const titleStyle =
    "font-family:Cambria,'Times New Roman',Times,serif;font-size:10pt;";
  const rowStyle = 'display:flex;gap:24px;margin-bottom:16px;';
  const wrapperStyle =
    'margin-top:40px;padding-top:20px;border-top:1px solid #ccc;';

  // Split into rows of 2
  const rows: string[] = [];
  for (let i = 0; i < populated.length; i += 2) {
    const pair = populated.slice(i, i + 2);
    const cells = pair
      .map(
        (s) =>
          `<div style="${blockStyle}">` +
          `<div style="${nameStyle}">${s.name}</div>` +
          `<div style="${titleStyle}">${s.title}</div>` +
          `</div>`
      )
      .join('');
    rows.push(`<div style="${rowStyle}">${cells}</div>`);
  }

  return `<div style="${wrapperStyle}">${rows.join('')}</div>`;
}

/**
 * Options for creating printable container
 */
interface PrintableContainerOptions extends Html2PdfExportOptions {
  /** Whether to exclude the header section from the content */
  excludeHeader?: boolean;
  /** Whether to exclude the footer section from the content (footer is added via jsPDF) */
  excludeFooter?: boolean;
}

/**
 * Create a hidden container with the editor content for PDF rendering.
 * @param editorElement - The editor HTML element containing the content
 * @param options - Export options including whether to exclude header/footer
 * @returns A hidden div element containing the printable content
 */
function createPrintableContainer(
  editorElement: HTMLElement,
  options: PrintableContainerOptions
): HTMLDivElement {
  const container = document.createElement('div');

  const extractedStyles = extractCurrentPageStyles();

  container.innerHTML = `
    <style>
      ${extractedStyles}
      ${PDF_PRINT_STYLES}
    </style>
    <div class="print-container"></div>
  `;

  const printContent = container.querySelector('.print-container');
  if (printContent && editorElement) {
    const proseMirror = editorElement.querySelector('.ProseMirror');
    if (proseMirror) {
      const clonedContent = proseMirror.cloneNode(true) as HTMLElement;

      if (options.excludeHeader) {
        const headerEl = clonedContent.querySelector(
          '[data-section-type="header"]'
        );
        if (headerEl) {
          headerEl.remove();
        }
      }

      if (options.excludeFooter) {
        const footerEl = clonedContent.querySelector(
          '[data-section-type="footer"]'
        );
        if (footerEl) {
          footerEl.remove();
        }
      }

      // Inject signer data into the signatures section
      if (options.signers && options.signers.length > 0) {
        const signersHtml = buildSignersHtml(options.signers);
        if (signersHtml) {
          const signaturesEl = clonedContent.querySelector(
            '[data-section-type="signatures"]'
          );
          if (signaturesEl) {
            signaturesEl.outerHTML = signersHtml;
          } else {
            clonedContent.insertAdjacentHTML('beforeend', signersHtml);
          }
        }
      }

      printContent.innerHTML = clonedContent.innerHTML;
    }
  }

  container.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    background: white;
  `;

  return container;
}

/** Estimated height of a page-number line in mm (9pt text + 4px padding, scale:2) */
const PAGE_NUM_HEIGHT_ESTIMATE_MM = 5;

/**
 * Get html2pdf options configured for A4 with margins.
 *
 * Top margin is `marginTop + topOverlayHeightMm + gap` when an overlay
 * (header or patient info) sits above the content on every page, otherwise
 * just `marginTop`.
 *
 * Bottom margin reserves enough room for the page number + gap + footer so
 * that content never flows into the overlay area.
 *
 * @param filename - The filename for the PDF
 * @param options - Export options
 * @param topOverlayHeightMm - Height of the tallest top overlay (header or patient info); increases top margin
 * @param _unused - Reserved (pass undefined)
 * @param footerHeightMm - Footer image height; increases bottom margin to fit footer + page number
 * @returns html2pdf configuration options
 */
function getHtml2PdfOptions(
  filename: string,
  options: Html2PdfExportOptions,
  topOverlayHeightMm?: number,
  _unused?: number,
  footerHeightMm?: number
) {
  // ── Top margin ────────────────────────────────────────────────────────────
  const topMargin = topOverlayHeightMm
    ? PDF_DIMENSIONS.marginTop + topOverlayHeightMm + HEADER_CONTENT_GAP_MM
    : PDF_DIMENSIONS.marginTop;

  // ── Bottom margin ─────────────────────────────────────────────────────────
  // Space needed from the page bottom edge up:
  //   footer bottom padding + footer height + page-number gap + page-number height
  const FOOTER_BOTTOM_PADDING_MM = 3;
  const PAGE_NUM_GAP_MM = 2;
  const neededBottomMargin = footerHeightMm
    ? FOOTER_BOTTOM_PADDING_MM +
      footerHeightMm +
      PAGE_NUM_GAP_MM +
      PAGE_NUM_HEIGHT_ESTIMATE_MM
    : 0;
  const bottomMargin = Math.max(
    PDF_DIMENSIONS.marginBottom,
    neededBottomMargin
  );

  return {
    margin: [
      topMargin,
      PDF_DIMENSIONS.marginRight,
      bottomMargin,
      PDF_DIMENSIONS.marginLeft,
    ] as [number, number, number, number],
    filename: `${filename}.pdf`,
    image: {
      type: 'jpeg' as const,
      quality: options.imageQuality ?? 0.98,
    },
    html2canvas: {
      scale: 3,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      letterRendering: true,
    },
    jsPDF: {
      unit: 'mm' as const,
      format: 'a4' as const,
      orientation: 'portrait' as const,
    },
    pagebreak: {
      mode: ['avoid-all', 'css', 'legacy'],
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: [
        '[data-section-type="header"]',
        '[data-section-type="signatures"]',
        '[data-section-type="patient-info"]',
        'table',
        'tr',
        'h1',
        'h2',
        'h3',
      ],
    },
  };
}

// =============================================================================
// Core PDF generation
// =============================================================================

/** Output mode for PDF generation */
type PdfOutputMode = 'blob' | 'save';

// Function overloads for generateAndOutputPdf
async function generateAndOutputPdf(
  editorElement: HTMLElement,
  options: Html2PdfExportOptions,
  outputMode: 'blob'
): Promise<Blob>;
async function generateAndOutputPdf(
  editorElement: HTMLElement,
  options: Html2PdfExportOptions,
  outputMode: 'save'
): Promise<undefined>;
/**
 * Core PDF generation with per-page overlays.
 *
 * Page layout when `repeatHeader: false` (default):
 *   Page 1   – header inline in content → content → page number → footer
 *   Pages 2+ – patient info box (top margin) → content → page number → footer
 *
 * Page layout when `repeatHeader: true`:
 *   All pages – header image (top margin) → content → page number → footer
 *
 * @param editorElement - The editor HTML element containing the content
 * @param options - PDF export configuration options
 * @param outputMode - Whether to return a blob or save to file
 * @returns Promise resolving to a PDF Blob (if 'blob') or undefined (if 'save')
 */
async function generateAndOutputPdf(
  editorElement: HTMLElement,
  options: Html2PdfExportOptions,
  outputMode: PdfOutputMode
): Promise<Blob | undefined> {
  const filename = options.filename || 'report';
  const repeatHeader = options.repeatHeader ?? false;
  const includePageNumbers = options.includePageNumbers ?? true;

  // ── 1. Capture footer as an image ────────────────────────────────────────────
  //    Create a temporary print container (with header + footer) so the footer
  //    is rendered with PDF_PRINT_STYLES applied before being captured.
  //    The footer is then removed from the final render container and re-added
  //    via jsPDF to every page as an overlay.
  let footerCapture: FooterCapture | null = null;
  const footerCaptureContainer = createPrintableContainer(editorElement, {
    ...options,
    excludeHeader: false,
    excludeFooter: false,
  });
  document.body.appendChild(footerCaptureContainer);

  try {
    const footerPrintContent = footerCaptureContainer.querySelector(
      '.print-container'
    ) as HTMLElement | null;
    if (footerPrintContent) {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      footerCapture = await captureFooterAsImage(footerPrintContent);
    }
  } finally {
    document.body.removeChild(footerCaptureContainer);
  }

  // ── 3. Optionally create patient info box image (pages 2+ when not repeating header) ──
  let patientInfoCapture: PatientInfoCapture | null = null;
  if (!repeatHeader && options.bindingData) {
    patientInfoCapture = await createPatientInfoImage(options.bindingData);
  }

  // ── 2. Capture the header as an image when:
  //       a) repeatHeader=true  → stamp on every page, or
  //       b) patientInfo available → stamp on page 1 only so the header starts
  //          at the same marginTop Y as the patient info box on pages 2+.
  //          The header is removed from HTML content in both cases.
  let headerCapture: HeaderCapture | null = null;
  if (repeatHeader || patientInfoCapture) {
    const headerContainer = createPrintableContainer(editorElement, {
      ...options,
      excludeHeader: false,
      excludeFooter: false,
    });
    document.body.appendChild(headerContainer);

    try {
      const headerPrintContent = headerContainer.querySelector(
        '.print-container'
      ) as HTMLElement | null;
      if (headerPrintContent) {
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
        headerCapture = await captureHeaderAsImage(headerPrintContent);
      }
    } finally {
      document.body.removeChild(headerContainer);
    }
  }

  // ── 4. Create the final render container ─────────────────────────────────────
  //    - Header: removed from content when repeatHeader=true (stamped on every
  //              page) OR when patientInfo is available (stamped on page 1 only
  //              so both header and patient-info start at the same marginTop Y).
  //    - Footer: always removed from content; added via jsPDF to every page.
  const excludeHeader = repeatHeader || patientInfoCapture !== null;
  const container = createPrintableContainer(editorElement, {
    ...options,
    excludeHeader,
    excludeFooter: true,
  });
  document.body.appendChild(container);

  try {
    const printContent = container.querySelector(
      '.print-container'
    ) as HTMLElement | null;
    if (!printContent) {
      throw new Error('Print container not found');
    }

    // Top overlay height for the top margin calculation:
    // - repeatHeader=true:  header height (header stamped on every page)
    // - repeatHeader=false: ONLY patient info height (patient info on pages 2+;
    //   the header on page 1 is a separate jsPDF overlay that may extend beyond
    //   the margin — a spacer below handles that)
    const topOverlayHeightMm = repeatHeader
      ? headerCapture?.scaledHeightMm
      : patientInfoCapture?.heightMm;

    // When the header is taller than the patient info box, the header jsPDF
    // overlay on page 1 would extend into the content area (because topMargin
    // is now sized for the shorter patient info).  A transparent spacer at the
    // very top of the content prevents real content from being drawn under the
    // header image on page 1.  On pages 2+ this spacer has already scrolled
    // off, so it has no effect there.
    if (!repeatHeader && headerCapture && patientInfoCapture && printContent) {
      const spacerHeightMm = Math.max(
        0,
        headerCapture.scaledHeightMm -
          patientInfoCapture.heightMm -
          HEADER_CONTENT_GAP_MM
      );
      if (spacerHeightMm > 0) {
        const spacer = document.createElement('div');
        spacer.style.height = `${spacerHeightMm}mm`;
        printContent.prepend(spacer);
      }
    }

    const html2pdfOptions = getHtml2PdfOptions(
      filename,
      options,
      topOverlayHeightMm,
      undefined,
      footerCapture?.scaledHeightMm ?? undefined
    );

    const worker = html2pdf().set(html2pdfOptions).from(printContent).toPdf();
    const pdf = await worker.get('pdf');

    // ── 5. Add per-page overlays ──────────────────────────────────────────────
    await addPdfOverlays(
      pdf,
      headerCapture,
      footerCapture,
      patientInfoCapture,
      {
        includePageNumbers,
        repeatHeader,
        headerOnPage1Only: !repeatHeader && patientInfoCapture !== null,
      }
    );

    if (outputMode === 'blob') {
      return await worker.outputPdf('blob');
    } else {
      await worker.save(`${filename}.pdf`);
    }
  } finally {
    document.body.removeChild(container);
  }
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Export the report editor content to PDF using html2pdf.js
 * @param editorElement - The editor HTML element containing the content
 * @param options - PDF export configuration options
 * @returns Promise resolving to a PDF Blob
 */
export async function exportReportToPdfHtml2Pdf(
  editorElement: HTMLElement,
  options: Html2PdfExportOptions
): Promise<Blob> {
  return generateAndOutputPdf(editorElement, options, 'blob') as Promise<Blob>;
}

/**
 * Download the PDF to the user's device using html2pdf.js
 * @param editorElement - The editor HTML element containing the content
 * @param options - PDF export configuration options
 */
export async function downloadReportPdfHtml2Pdf(
  editorElement: HTMLElement,
  options: Html2PdfExportOptions
): Promise<void> {
  await generateAndOutputPdf(editorElement, options, 'save');
}

/**
 * Open PDF in a new tab for preview
 * @param editorElement - The editor HTML element containing the content
 * @param options - PDF export configuration options
 */
export async function openPdfPreviewInNewTab(
  editorElement: HTMLElement,
  options: Html2PdfExportOptions
): Promise<void> {
  const blob = await exportReportToPdfHtml2Pdf(editorElement, options);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');

  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60000);
}

/**
 * Open browser print dialog for the report.
 * NOTE: This function is still in active use (not deprecated) as it provides
 * native browser print functionality which is independent of PDF generation method.
 * @param editorElement - The editor HTML element containing the content
 */
export function printReport(editorElement: HTMLElement): void {
  const proseMirror = editorElement.querySelector('.ProseMirror');
  if (!proseMirror) {
    console.error('ProseMirror element not found');
    return;
  }

  const printHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Report</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 20mm 15mm 25mm 15mm;
          }

          @media print {
            body {
              font-family: Cambria, 'Times New Roman', Times, serif;
              font-size: 12pt;
              line-height: 1.6;
              color: #000;
              margin: 0;
              padding: 0;
            }

            table {
              border-collapse: collapse;
              width: 100%;
              page-break-inside: avoid;
            }

            th, td {
              border: 1px solid #000;
              padding: 8px 12px;
            }

            [data-section-type="header"] {
              border: 2px solid #000;
              padding: 16px;
              margin-bottom: 20px;
            }

            [data-section-type="signatures"] {
              page-break-inside: avoid;
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ccc;
            }

            h1 { font-size: 24pt; }
            h2 { font-size: 18pt; }
            h3 { font-size: 14pt; }
          }
        </style>
      </head>
      <body>
        ${proseMirror.innerHTML}
      </body>
    </html>
  `;

  const printFrame = document.createElement('iframe');
  printFrame.style.cssText =
    'position: absolute; left: -9999px; width: 0; height: 0;';
  document.body.appendChild(printFrame);

  const frameDoc = printFrame.contentWindow?.document;
  if (frameDoc) {
    frameDoc.open();
    frameDoc.write(printHTML);
    frameDoc.close();

    printFrame.onload = () => {
      printFrame.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 1000);
    };
  }
}
